/**
 * @file     GateStepTransition.tsx
 * @purpose  Directional step animation for the multi-step auth gates. Forward
 *           slides the incoming step in from the right, back slides it in from
 *           the left, and the container animates its own height between steps
 *           so the dialog never snaps. Honours prefers-reduced-motion by
 *           dropping to a plain crossfade with no height animation.
 */
'use client';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import { motionTokens } from '@/design-system/tokens';

const { enterDuration, heightDuration, offset: OFFSET } = motionTokens.stepTransition;
const { easing } = motionTokens;

interface Props {
  /** Identity of the current step. A change drives the transition. */
  stepKey: string;
  /** 'forward' slides in from the right, 'back' from the left. */
  direction: 'forward' | 'back';
  children: React.ReactNode;
}

export function GateStepTransition({ stepKey, direction, children }: Props) {
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const innerRef = useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = useState<number | 'auto'>('auto');
  const [entering, setEntering] = useState(false);

  // Re-arm the enter animation whenever the step changes.
  useLayoutEffect(() => {
    if (reduceMotion) return;
    setEntering(true);
    const id = requestAnimationFrame(() => setEntering(false));
    return () => cancelAnimationFrame(id);
  }, [stepKey, reduceMotion]);

  // Track the natural height of the active step so the shell can animate to it.
  // Steps grow when validation errors appear, so this observes rather than
  // measuring once.
  useEffect(() => {
    if (reduceMotion) return;
    const el = innerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const obs = new ResizeObserver(() => setHeight(el.offsetHeight));
    obs.observe(el);
    setHeight(el.offsetHeight);
    return () => obs.disconnect();
  }, [stepKey, reduceMotion]);

  if (reduceMotion) {
    return <Box>{children}</Box>;
  }

  const offset = direction === 'forward' ? OFFSET : -OFFSET;

  return (
    <Box
      sx={{
        position: 'relative',
        height,
        overflow: 'hidden',
        transition: `height ${heightDuration}ms ${easing.standard}`,
      }}
    >
      <Box
        key={stepKey}
        ref={innerRef}
        sx={{
          opacity: entering ? 0 : 1,
          transform: entering ? `translateX(${offset}px)` : 'translateX(0)',
          transition: `opacity ${enterDuration}ms ${easing.decelerate}, transform ${enterDuration}ms ${easing.decelerate}`,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
